/**
 * Re-Routing Service
 * Servico de re-roteirizacao dinamica
 *
 * Funcionalidades:
 * - Recalcular rota a partir da posicao atual
 * - Considerar entregas ja realizadas
 * - Manter historico de re-roteirizacoes
 * - Comparar nova rota com original
 */

import { optimizeRoutes, calculateRoute } from '../../common/services/routingService';

// =======================================
// Constantes
// =======================================

export const REROUTE_REASON = {
  TRAFFIC: 'traffic',
  ROAD_BLOCKED: 'road_blocked',
  VEHICLE_ISSUE: 'vehicle_issue',
  CUSTOMER_REQUEST: 'customer_request',
  PRIORITY_CHANGE: 'priority_change',
  NEW_DELIVERY: 'new_delivery',
  SKIP_DELIVERY: 'skip_delivery',
  MANUAL: 'manual',
  AUTO_DEVIATION: 'auto_deviation',
};

export const REROUTE_REASON_LABELS = {
  [REROUTE_REASON.TRAFFIC]: 'Transito intenso',
  [REROUTE_REASON.ROAD_BLOCKED]: 'Via bloqueada',
  [REROUTE_REASON.VEHICLE_ISSUE]: 'Problema no veiculo',
  [REROUTE_REASON.CUSTOMER_REQUEST]: 'Solicitacao do cliente',
  [REROUTE_REASON.PRIORITY_CHANGE]: 'Mudanca de prioridade',
  [REROUTE_REASON.NEW_DELIVERY]: 'Nova entrega adicionada',
  [REROUTE_REASON.SKIP_DELIVERY]: 'Entrega pulada',
  [REROUTE_REASON.MANUAL]: 'Solicitacao manual',
  [REROUTE_REASON.AUTO_DEVIATION]: 'Desvio automatico detectado',
};

// Rate limiting: cooldown entre re-roteirizacoes (ms)
const REROUTE_COOLDOWN_MS = 30_000;
let lastRerouteTimestamp = 0;

// =======================================
// Funcoes Auxiliares
// =======================================

/**
 * Filtra paradas pendentes (nao completadas/puladas)
 */
const filterPendingStops = (steps, completedStopIds = []) => {
  return steps.filter((step) => {
    // Manter start e end
    if (step.type === 'start' || step.type === 'end') {
      return true;
    }
    // Filtrar jobs ja completados
    if (step.type === 'job') {
      return !completedStopIds.includes(step.id);
    }
    return true;
  });
};

/**
 * Converte step para formato de job do VROOM
 */
const stepToVroomJob = (step, index) => {
  const location = step.location || {};
  return {
    id: step.id || index + 1,
    location: [location.lng || location[0], location.lat || location[1]],
    service: step.serviceTime || step.service || 300,
    delivery: step.weight ? [step.weight] : [1],
    priority: step.priority === 'urgent' ? 10 : step.priority === 'high' ? 7 : step.priority === 'low' ? 1 : 4,
    description: step.name || step.address || `Parada ${index + 1}`,
    // Manter dados originais
    _original: step,
  };
};

/**
 * Converte veiculo para formato VROOM
 */
const vehicleToVroom = (vehicle, currentPosition, endPosition) => {
  return {
    id: vehicle.id || 1,
    start: [currentPosition.longitude, currentPosition.latitude],
    end: endPosition ? [endPosition.lng || endPosition[0], endPosition.lat || endPosition[1]] : [currentPosition.longitude, currentPosition.latitude],
    capacity: vehicle.capacity ? [vehicle.capacity] : [100],
    profile: 'car',
  };
};

// =======================================
// Funcao Principal de Re-roteirizacao
// =======================================

/**
 * Recalcula a rota a partir da posicao atual
 *
 * @param {Object} params - Parametros
 * @param {Object} params.currentPosition - Posicao atual do veiculo {latitude, longitude}
 * @param {Object} params.originalRoute - Rota original com steps
 * @param {Object} params.vehicle - Dados do veiculo
 * @param {Array} params.completedStopIds - IDs das paradas ja completadas
 * @param {Object} params.options - Opcoes adicionais
 * @returns {Object} Nova rota otimizada
 */
export const reRouteFromCurrentPosition = async ({
  currentPosition,
  originalRoute,
  vehicle,
  completedStopIds = [],
  options = {},
}) => {
  const {
    reason = REROUTE_REASON.MANUAL,
    keepEndPoint = true,
    useVROOM = true,
  } = options;

  if (!currentPosition || !originalRoute?.steps) {
    throw new Error('Posicao atual e rota original sao obrigatorios');
  }

  // Rate limiting — impedir chamadas frequentes ao VROOM/OSRM
  const now = Date.now();
  const elapsed = now - lastRerouteTimestamp;
  if (elapsed < REROUTE_COOLDOWN_MS) {
    const waitSec = Math.ceil((REROUTE_COOLDOWN_MS - elapsed) / 1000);
    throw new Error(`Aguarde ${waitSec}s antes de recalcular a rota`);
  }
  lastRerouteTimestamp = now;

  // 1. Filtrar paradas pendentes
  const pendingSteps = filterPendingStops(originalRoute.steps, completedStopIds);
  const jobSteps = pendingSteps.filter((s) => s.type === 'job');


  if (jobSteps.length === 0) {
    // Nao ha mais paradas pendentes
    return {
      success: true,
      route: null,
      message: 'Todas as entregas foram concluidas',
      noMoreStops: true,
    };
  }

  // 2. Encontrar ponto final
  const endStep = originalRoute.steps.find((s) => s.type === 'end');
  const endPosition = keepEndPoint && endStep?.location ? endStep.location : null;

  // 3. Preparar dados para otimizacao
  const vroomVehicle = vehicleToVroom(vehicle, currentPosition, endPosition);
  const vroomJobs = jobSteps.map((step, idx) => stepToVroomJob(step, idx));

  try {
    let newRoute;

    if (useVROOM && jobSteps.length > 1) {
      // Usar VROOM para otimizacao

      const vroomRequest = {
        vehicles: [vroomVehicle],
        jobs: vroomJobs,
      };

      const result = await optimizeRoutes(vroomRequest);

      if (!result || !result.routes || result.routes.length === 0) {
        throw new Error('VROOM nao retornou rotas');
      }

      newRoute = result.routes[0];

      // Mapear steps de volta para os dados originais
      newRoute.steps = newRoute.steps.map((step) => {
        if (step.type === 'job') {
          const originalJob = vroomJobs.find((j) => j.id === step.id);
          if (originalJob?._original) {
            return {
              ...step,
              ...originalJob._original,
              arrival: step.arrival,
              distance: step.distance,
              duration: step.duration,
            };
          }
        }
        return step;
      });
    } else {
      // Usar OSRM simples para rota direta

      const waypoints = [
        { lat: currentPosition.latitude, lng: currentPosition.longitude },
        ...jobSteps.map((s) => ({
          lat: s.location?.lat || s.location?.[1],
          lng: s.location?.lng || s.location?.[0],
        })),
      ];

      if (endPosition) {
        waypoints.push({
          lat: endPosition.lat || endPosition[1],
          lng: endPosition.lng || endPosition[0],
        });
      }

      const osrmResult = await calculateRoute(waypoints, 'car', { overview: 'full' });

      // Construir steps a partir do resultado OSRM
      let accumulatedTime = 0;
      const steps = [
        {
          type: 'start',
          location: { lat: currentPosition.latitude, lng: currentPosition.longitude },
          arrival: 0,
        },
        ...jobSteps.map((step, idx) => {
          // Estimar tempo proporcional
          const proportion = (idx + 1) / (jobSteps.length + 1);
          accumulatedTime = Math.round(osrmResult.duration * proportion);

          return {
            ...step,
            type: 'job',
            arrival: accumulatedTime,
            estimatedArrival: new Date(Date.now() + accumulatedTime * 1000),
          };
        }),
      ];

      if (endPosition) {
        steps.push({
          type: 'end',
          location: endPosition,
          arrival: osrmResult.duration,
        });
      }

      newRoute = {
        vehicleId: vehicle.id,
        steps,
        distance: osrmResult.distance,
        duration: osrmResult.duration,
        geometry: osrmResult.geometry,
      };
    }

    // 4. Calcular diferencas
    const originalDistance = originalRoute.distance || 0;
    const originalDuration = originalRoute.duration || 0;
    const distanceDiff = newRoute.distance - originalDistance;
    const durationDiff = newRoute.duration - originalDuration;

    // 5. Retornar resultado
    return {
      success: true,
      route: newRoute,
      comparison: {
        originalDistance,
        newDistance: newRoute.distance,
        distanceDiff,
        distanceChangePercent: originalDistance > 0 ? ((distanceDiff / originalDistance) * 100).toFixed(1) : 0,
        originalDuration,
        newDuration: newRoute.duration,
        durationDiff,
        durationChangePercent: originalDuration > 0 ? ((durationDiff / originalDuration) * 100).toFixed(1) : 0,
      },
      reason,
      timestamp: new Date(),
      fromPosition: currentPosition,
      pendingStopsCount: jobSteps.length,
    };
  } catch (error) {
    console.error('[ReRouting] Error:', error);
    throw new Error(`Falha ao recalcular rota: ${error.message}`);
  }
};

/**
 * Adiciona uma nova entrega a rota existente
 */
export const addDeliveryToRoute = async ({
  currentPosition,
  originalRoute,
  vehicle,
  newDelivery,
  completedStopIds = [],
}) => {
  // Adicionar nova entrega aos steps pendentes
  const newStep = {
    type: 'job',
    id: newDelivery.id || `new-${Date.now()}`,
    name: newDelivery.name,
    address: newDelivery.address,
    location: {
      lat: newDelivery.latitude || newDelivery.lat,
      lng: newDelivery.longitude || newDelivery.lng,
    },
    serviceTime: newDelivery.serviceTime || 300,
    priority: newDelivery.priority || 'normal',
    weight: newDelivery.weight || 1,
    isNew: true,
  };

  const modifiedRoute = {
    ...originalRoute,
    steps: [...originalRoute.steps, newStep],
  };

  return reRouteFromCurrentPosition({
    currentPosition,
    originalRoute: modifiedRoute,
    vehicle,
    completedStopIds,
    options: {
      reason: REROUTE_REASON.NEW_DELIVERY,
      useVROOM: true,
    },
  });
};

/**
 * Remove uma entrega da rota e recalcula
 */
export const skipDeliveryAndReRoute = async ({
  currentPosition,
  originalRoute,
  vehicle,
  skipStopId,
  completedStopIds = [],
}) => {
  // Adicionar a parada pulada aos completados
  const updatedCompletedIds = [...completedStopIds, skipStopId];

  return reRouteFromCurrentPosition({
    currentPosition,
    originalRoute,
    vehicle,
    completedStopIds: updatedCompletedIds,
    options: {
      reason: REROUTE_REASON.SKIP_DELIVERY,
      useVROOM: true,
    },
  });
};

// =======================================
// Historico de Re-roteirizacoes
// =======================================

/**
 * Classe para gerenciar historico de re-roteirizacoes
 */
export class ReRoutingHistory {
  constructor(planId) {
    this.planId = planId;
    this.history = [];
  }

  /**
   * Adiciona uma entrada ao historico
   */
  add(entry) {
    const historyEntry = {
      id: `reroute-${Date.now()}`,
      timestamp: new Date(),
      planId: this.planId,
      ...entry,
    };

    this.history.push(historyEntry);

    // Persistir no sessionStorage (limpo ao fechar aba)
    this.save();

    return historyEntry;
  }

  /**
   * Retorna todo o historico
   */
  getAll() {
    return [...this.history];
  }

  /**
   * Retorna a ultima re-roteirizacao
   */
  getLast() {
    return this.history[this.history.length - 1] || null;
  }

  /**
   * Conta re-roteirizacoes
   */
  getCount() {
    return this.history.length;
  }

  /**
   * Calcula estatisticas
   */
  getStats() {
    if (this.history.length === 0) {
      return {
        count: 0,
        totalDistanceAdded: 0,
        totalDurationAdded: 0,
        reasons: {},
      };
    }

    const reasons = {};
    let totalDistanceAdded = 0;
    let totalDurationAdded = 0;

    this.history.forEach((entry) => {
      if (entry.comparison) {
        totalDistanceAdded += entry.comparison.distanceDiff || 0;
        totalDurationAdded += entry.comparison.durationDiff || 0;
      }
      reasons[entry.reason] = (reasons[entry.reason] || 0) + 1;
    });

    return {
      count: this.history.length,
      totalDistanceAdded,
      totalDurationAdded,
      reasons,
    };
  }

  /**
   * Salva no sessionStorage
   */
  save() {
    try {
      const key = `rerouting-history-${this.planId}`;
      sessionStorage.setItem(key, JSON.stringify(this.history));
    } catch (error) {
      console.warn('[ReRoutingHistory] Failed to save:', error);
    }
  }

  /**
   * Carrega do sessionStorage
   */
  load() {
    try {
      const key = `rerouting-history-${this.planId}`;
      const stored = sessionStorage.getItem(key);
      if (stored) {
        this.history = JSON.parse(stored);
      }
    } catch (error) {
      console.warn('[ReRoutingHistory] Failed to load:', error);
      this.history = [];
    }
    return this;
  }

  /**
   * Limpa o historico
   */
  clear() {
    this.history = [];
    try {
      const key = `rerouting-history-${this.planId}`;
      sessionStorage.removeItem(key);
    } catch (error) {
      console.warn('[ReRoutingHistory] Failed to clear:', error);
    }
  }
}

// =======================================
// Factory
// =======================================

/**
 * Cria uma instancia de historico para um plano
 */
export const createReRoutingHistory = (planId) => {
  return new ReRoutingHistory(planId).load();
};

export default {
  REROUTE_REASON,
  REROUTE_REASON_LABELS,
  reRouteFromCurrentPosition,
  addDeliveryToRoute,
  skipDeliveryAndReRoute,
  ReRoutingHistory,
  createReRoutingHistory,
};
